/**
 * E018-T004: Global Execution Listener Hook
 *
 * Listens for automation-run events from Canvas Node
 * and triggers execution via ExecutionService.
 *
 * This hook should be mounted once in the app root to handle
 * run button clicks from the Canvas Node.
 */

import { useService } from '@toeverything/infra';
import { useEffect } from 'react';

import { WorkspaceService } from '../../workspace';
import { ExecutionService } from '../services/execution';

/**
 * Event detail for automation-run custom event
 */
export interface AutomationRunEventDetail {
  blockId: string;
  docId: string;
  mode: 'execute';
}

/**
 * Event detail for automation-run-complete custom event
 * Uses RunStatus type: 'success' | 'error' | 'running' | null
 */
export interface AutomationRunCompleteDetail {
  blockId: string;
  status: 'success' | 'error';
}

declare global {
  interface WindowEventMap {
    'automation-run': CustomEvent<AutomationRunEventDetail>;
    'automation-run-complete': CustomEvent<AutomationRunCompleteDetail>;
  }
}

/**
 * Hook that listens for automation-run events and triggers execution.
 * Mount this once in the app root (e.g., workspace-side-effects).
 */
export function useExecutionListener(): void {
  const executionService = useService(ExecutionService);
  const workspaceService = useService(WorkspaceService);

  useEffect(() => {
    const handleRun = async (event: CustomEvent<AutomationRunEventDetail>) => {
      const { blockId, docId, mode } = event.detail;

      const workspaceId = workspaceService.workspace.id;

      console.log('[ExecutionListener] Received run event:', {
        blockId,
        docId,
        mode,
        workspaceId,
      });

      let finalStatus: 'success' | 'error' = 'error';

      try {
        const result = await executionService.executeViaBackend(
          workspaceId,
          docId,
          blockId,
          mode
        );

        if (result.success) {
          console.log('[ExecutionListener] Execution completed:', {
            rowsProcessed: result.rowsProcessed,
            rowsWritten: result.rowsWritten,
          });
          finalStatus = 'success';
        } else {
          console.error('[ExecutionListener] Execution failed:', result.error);
          finalStatus = 'error';
        }
      } catch (err) {
        console.error('[ExecutionListener] Failed to execute:', err);
        finalStatus = 'error';
      }

      // E018-T004: Dispatch completion event for Canvas Node to update status
      window.dispatchEvent(
        new CustomEvent('automation-run-complete', {
          detail: { blockId, status: finalStatus },
          bubbles: true,
        })
      );
    };

    // Wrap async handler to avoid Promise return in event listener
    const runHandler = (event: CustomEvent<AutomationRunEventDetail>) => {
      handleRun(event).catch(console.error);
    };

    window.addEventListener('automation-run', runHandler as EventListener);

    return () => {
      window.removeEventListener('automation-run', runHandler as EventListener);
    };
  }, [executionService, workspaceService]);
}
